/**
 * 提醒中心**端侧纯逻辑**（渲染输入 → 渲染结构）—— H 域 · 端侧
 *
 * ## 🔴 本文件**不做任何口径判定**
 *
 * 状态 / 分组 / 关键-常规 / 合并 —— **全部由服务端算好**（共享层 `nucleus/reminder.ts`）。
 * 本文件只回答三件**渲染才需要**的事：
 *
 * | 问题 | 函数 |
 * |---|---|
 * | 三档按什么顺序摆？ | `GROUP_RENDER_ORDER` / `groupRank()` |
 * | 某一组里有哪些条目？ | `itemsOfGroup()` |
 * | 「标记已读」要提交哪些 id？ | `storageIdsOf()`（🔴 **合并条要展开**） |
 *
 * ## 🔴 为什么「合并条要展开」
 *
 * 合并是**展示层**行为（`AC-H1.9`）：一条合并卡背后是 N 行 `reminder_instances`。
 * 「标记已读」是按**行**落库的（`POST /api/reminders/read` 收 `ids`）
 * ⇒ 若只提交代表 id，**其余 N−1 行会一直是未读**（下次刷新又冒出来）。
 * 这也正是「`mergedIds` 必须保留全部原 id」的原因（`DR-038` §4）。
 */

import {
  ReminderGroup,
  type ReminderListResponse,
  type ReminderView,
} from '@jiaying/shared';

/** 🔴 三档的**固定渲染顺序**（原型：需要你处理 → 知道就好 → 更早；**不按时间堆砌**） */
export const GROUP_RENDER_ORDER: readonly ReminderGroup[] = [
  ReminderGroup.Action,
  ReminderGroup.Info,
  ReminderGroup.Earlier,
];

/** 组的排序权重（越小越靠前）；未知组排最后（**不抛错** —— 服务端新增组时页面不该白屏） */
export function groupRank(group: ReminderGroup): number {
  const i = GROUP_RENDER_ORDER.indexOf(group);
  return i === -1 ? GROUP_RENDER_ORDER.length : i;
}

/** 取某一组的条目（保持服务端给的顺序 —— 🔴 **端侧不重排**，重排 = 第二份排序口径） */
export function itemsOfGroup(
  items: readonly ReminderView[],
  group: ReminderGroup,
): ReminderView[] {
  return items.filter((i) => i.group === group);
}

/** 🔴 一条提醒对应的**全部落库 id**（合并条展开 —— 见文件头） */
export function storageIdsOf(item: ReminderView): string[] {
  return item.mergedIds.length > 0 ? [...item.mergedIds] : [item.id];
}

/**
 * 空态种类 —— **两个空态文案必须不同**：
 * - `none`：整个中心都没有提醒；
 * - `no-earlier`：有提醒，但「更早」档为空（用户主动展开历史时才看得到）。
 *
 * 🔴 把两者混成一个空态，会让"今天很太平"与"历史没加载"看起来一样。
 */
export function emptyKind(
  list: ReminderListResponse,
  includeEarlier: boolean,
): 'none' | 'no-earlier' | null {
  if (list.items.length === 0) return 'none';
  if (!includeEarlier) return null;
  const earlierCount = itemsOfGroup(list.items, ReminderGroup.Earlier).length;
  return earlierCount === 0 ? 'no-earlier' : null;
}

/** 分组小结 → 渲染用（保证三组**都出现**，`count: 0` 的组照常占位 —— 原型如此） */
export function summariesFor(
  list: ReminderListResponse,
  includeEarlier: boolean,
): { key: ReminderGroup; count: number }[] {
  const order = includeEarlier
    ? GROUP_RENDER_ORDER
    : GROUP_RENDER_ORDER.filter((g) => g !== ReminderGroup.Earlier);
  return order.map((g) => ({
    key: g,
    count: list.groups.find((x) => x.key === g)?.count ?? 0,
  }));
}
